import { ModalDialog } from 'components/ModalDialog';
import { Steps } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import type { CSSProperties, ForwardedRef } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import type { IAddWorkOrderPriceProps, IObjectTabFilters, TCommonObj, TControlRef } from './helpersAndTypes';
import {
    DEFAULT_ADD_UER_STEPS,
    getItemCompleted,
    getItemDisabledDefault,
    getStepLabel,
    groupWorkOrderObjectsByParentId,
} from './helpersAndTypes';
import { AddWorkOrderUerTabContent } from './TabContent';

import Styles from './AddWorkUerFrom.module.css';

export const AddWorkOrderPriceForm = forwardRef(
    <TObj extends TCommonObj>(props: IAddWorkOrderPriceProps<TObj>, ref: ForwardedRef<TControlRef>) => {
        const {
            objects,
            onClose,
            isLoading,
            stepsContent,
            getItemDisabled = getItemDisabledDefault,
            constructionTypeId,
            dataSource,
            searchString,
            onSearchStringClear,
            onSearchStringChange,
            onSearch,
            filters,
            checkedDataSender,
            initiallyCheckedLeafs,
            initiallyCheckedObj,
            extraColumnDefs,
            getObjItemLabel,
            getObjItemDisabled,
            filterVariables,
            changeFilterHandler,
        } = props;

        const tableApiRef = useRef<AgGridReact>(null);

        const [activeStep, setActiveStep] = useState(DEFAULT_ADD_UER_STEPS[0]);
        const [objectTabFilters, setObjectTabFilters] = useState<IObjectTabFilters>({});
        const [checkedObjects, setCheckedObjects] = useState<{ [key: number]: boolean | 'intermediate' }>({});
        const [checkedPrices, setCheckedPrices] = useState<{ [key: number]: boolean }>({});

        const onChangeStep = useCallback(({ value }: { value: (typeof DEFAULT_ADD_UER_STEPS)[number] }) => {
            setActiveStep((prevState) => {
                if (value?.stepId !== prevState?.stepId) {
                    setCheckedPrices({});
                }

                return value;
            });
        }, []);

        const onChangeObjectTabFilters = useCallback((value: IObjectTabFilters) => {
            setObjectTabFilters((prevState) => {
                if (value?.object?.id && value?.object?.id !== prevState?.object?.id) {
                    setCheckedObjects({});
                }

                return { ...prevState, ...value };
            });
        }, []);

        const objGroups = useMemo(() => groupWorkOrderObjectsByParentId(objects ?? []), [objects]);

        //  для установки состояния всех дочерних объектов
        const setChildrenState = useCallback(
            (parentId: number, state: boolean) => {
                const children = objGroups[parentId];

                if (children) {
                    children.forEach((child) => {
                        setCheckedObjects((prev) => ({ ...prev, [child.id]: state }));
                        setChildrenState(child.id, state);
                    });
                }
            },
            [objGroups],
        );

        //  для установки состояния родительских объектов
        const updateParentState = useCallback(
            (childId: number) => {
                const child = objects?.find((obj) => obj.id === childId);

                if (child?.parentId !== null) {
                    const siblings = child?.parentId ? objGroups[child.parentId] : [];

                    setCheckedObjects((prev) => {
                        const allChecked = siblings.every((sibling) => prev[sibling.id] === true);
                        const someChecked = siblings.some((sibling) => prev[sibling.id]);

                        if (child?.parentId) {
                            let state: boolean | 'intermediate' = false;

                            if (allChecked) {
                                state = true;
                            } else if (someChecked) {
                                state = 'intermediate';
                            }

                            return {
                                ...prev,
                                [child.parentId]: state,
                            };
                        }

                        return prev;
                    });

                    if (child) {
                        updateParentState(child.parentId);
                    }
                }
            },
            [objGroups, objects],
        );

        const onChangeCheckedObjects = useCallback(
            (value: { [key: number]: boolean }) => {
                const [currentKey, currentValue] = Object.entries(value)[0];
                const key = Number(currentKey);

                setCheckedObjects((prev) => ({ ...prev, [key]: currentValue }));
                setChildrenState(key, currentValue);
                updateParentState(key);
            },
            [setChildrenState, updateParentState],
        );

        const onChangeCheckedPrices = useCallback((value: { [key: number]: boolean }) => {
            setCheckedPrices((prevState) => ({ ...prevState, ...value }));
        }, []);

        const isNextStepDisabled = useMemo(
            () =>
                !(
                    objectTabFilters?.object?.id &&
                    Object.values(checkedObjects ?? {})?.some((checked) => checked) &&
                    objectTabFilters?.chapter?.id
                ),
            [checkedObjects, objectTabFilters?.chapter?.id, objectTabFilters?.object?.id],
        );

        const onStepClickCreator = useCallback(
            (step: (typeof DEFAULT_ADD_UER_STEPS)[number]) => () => {
                onChangeStep({ value: step });
            },
            [onChangeStep],
        );

        const checkedLeafObjects = useMemo(
            () =>
                objects
                    ?.filter(
                        (o) =>
                            o?.isLeaf &&
                            Object.entries(checkedObjects ?? {}).some(
                                ([id, checked]) => Number(id) === o.id && checked,
                            ),
                    )
                    ?.map((r) => r.id) ?? [],
            [checkedObjects, objects],
        );

        const uerRowBaseRowIds = useMemo(
            () =>
                Object.entries(checkedPrices ?? {})
                    ?.filter(([, checked]) => checked)
                    ?.map(([id]) => Number(id)),
            [checkedPrices],
        );

        useEffect(() => {
            checkedDataSender({
                objectTabFilters,
                checkedLeafObjects,
                uerRowBaseRowIds,
            });
        }, [checkedLeafObjects, checkedDataSender, uerRowBaseRowIds, objectTabFilters]);

        useEffect(() => {
            if (initiallyCheckedLeafs && Object.values(initiallyCheckedLeafs ?? {})?.length) {
                onChangeCheckedObjects(initiallyCheckedLeafs);
            }
        }, [initiallyCheckedLeafs, onChangeCheckedObjects]);

        useEffect(() => {
            if (initiallyCheckedObj) {
                setObjectTabFilters((prev) => ({
                    ...prev,
                    object: { id: initiallyCheckedObj?.id, label: initiallyCheckedObj?.name },
                }));
            }
        }, [initiallyCheckedLeafs, initiallyCheckedObj, onChangeCheckedObjects]);

        useImperativeHandle(ref, () => ({
            refreshAddDataTable: () => tableApiRef?.current?.api?.refreshInfiniteCache(),
            setCheckedPrices,
        }));

        return (
            <ModalDialog
                isOpen
                title="Добавление расценки из прейскуранта расценок"
                onClose={onClose}
                withTitleCloseButton
                isCommit={isLoading}
                className={Styles.modal}
                isOnClickOutsideDisable
                withBottomButtons={false}
                style={
                    {
                        '--modal_width': activeStep?.stepId === 0 ? '600px' : '1200px',
                    } as CSSProperties
                }
            >
                <div className={Styles.modalBody}>
                    <Steps
                        items={DEFAULT_ADD_UER_STEPS}
                        value={activeStep}
                        onChange={onChangeStep}
                        getItemLabel={getStepLabel}
                        getItemDisabled={getItemDisabled(isNextStepDisabled)}
                        getItemCompleted={getItemCompleted(activeStep)}
                        className={Styles.steps}
                        size="s"
                    />
                    <AddWorkOrderUerTabContent
                        ref={tableApiRef}
                        workOrderObjects={objects}
                        constructionTypeId={constructionTypeId}
                        activeStep={activeStep}
                        objectTabFilters={objectTabFilters}
                        onChangeObjectTabFilters={onChangeObjectTabFilters}
                        checkedObjects={checkedObjects}
                        onChangeCheckedObjects={onChangeCheckedObjects}
                        checkedPrices={checkedPrices}
                        onChangeCheckedPrices={onChangeCheckedPrices}
                        onSearch={onSearch}
                        dataSource={dataSource}
                        onSearchStringChange={onSearchStringChange}
                        filters={filters}
                        searchString={searchString}
                        onSearchStringClear={onSearchStringClear}
                        extraColumnDefs={extraColumnDefs}
                        getObjItemLabel={getObjItemLabel}
                        getObjItemDisabled={getObjItemDisabled}
                        filterVariables={filterVariables}
                        changeFilterHandler={changeFilterHandler}
                    />
                </div>
                <div className={Styles.footerBlock}>{stepsContent?.[activeStep?.stepId]?.(onStepClickCreator)}</div>
            </ModalDialog>
        );
    },
);

AddWorkOrderPriceForm.displayName = 'AddWorkOrderChangeUer';
